/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Scales, ArrowsClockwise, Warning, FileArrowUp, Receipt,
} from "@phosphor-icons/react";

const fmt = (v) => `£${(Number(v) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Firm-wide accounting dashboard. Top-level /accounts route.
 *
 *   • Tabs: Trial balance / SRA breaches / Unbilled disbursements / Bank import
 *   • All reads are pure GETs; bank import is a multipart POST.
 *
 * Per-case ledgers live on each case's Accounts tab — this page is the
 * firm-level cockpit.
 */
export default function AccountsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("drafts");
  const [trial, setTrial] = useState(null);
  const [breaches, setBreaches] = useState(null);
  const [unbilled, setUnbilled] = useState(null);
  const [bankRows, setBankRows] = useState([]);
  const [bankAccountCode, setBankAccountCode] = useState("1010");
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [draftStatus, setDraftStatus] = useState("pending");
  const [draftKind, setDraftKind] = useState("invoices"); // 'invoices' or 'journal'
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectingKind, setRejectingKind] = useState("invoices");
  const [rejectReason, setRejectReason] = useState("");
  const [coa, setCoa] = useState([]);
  const [approvingReq, setApprovingReq] = useState(null);
  const [drAccount, setDrAccount] = useState("");
  const [crAccount, setCrAccount] = useState("");
  const [payees, setPayees] = useState([]);
  const [editingPayee, setEditingPayee] = useState(null);
  const [payeeForm, setPayeeForm] = useState({ payee: "", debit_account: "", credit_account: "" });

  const loadTrial = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/accounts/trial-balance");
      setTrial(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, []);

  const loadBreaches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/accounts/sra-breaches");
      setBreaches(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, []);

  const loadUnbilled = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/accounts/unbilled-disbursements");
      setUnbilled(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, []);

  const loadBankRows = useCallback(async (code) => {
    setLoading(true);
    try {
      const { data } = await API.get(`/accounts/bank-rows?account_code=${code}`);
      setBankRows(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, []);

  const loadDrafts = useCallback(async (status, kind) => {
    setLoading(true);
    try {
      const url = kind === "journal"
        ? `/accounts/journal-requests?status=${status}`
        : `/accounts/draft-invoices?status=${status}`;
      const { data } = await API.get(url);
      setDrafts(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  }, []);

  const approveDraft = useCallback(async (id, kind) => {
    try {
      if (kind === "journal") {
        // For simple-form journal requests, admin must pick the accounts.
        // Open the approval dialog instead of immediately approving.
        const found = drafts.find((r) => r.id === id);
        if (found && found.kind === "simple") {
          setApprovingReq(found);
          // Prefill from payee memory if we've approved the same payee
          // before — admin can still override before confirming.
          const payee = found.simple_form?.payee || "";
          let prefillDr = "";
          let prefillCr = "";
          if (payee) {
            try {
              const { data } = await API.get(`/accounts/payee-memory?payee=${encodeURIComponent(payee)}`);
              if (data) {
                prefillDr = data.debit_account || "";
                prefillCr = data.credit_account || "";
                if (data.use_count > 0) {
                  toast.info(`Suggested from last ${data.use_count} approval(s) for "${data.payee_display}"`);
                }
              }
            } catch { /* ignore — admin still picks manually */ }
          }
          setDrAccount(prefillDr);
          setCrAccount(prefillCr);
          return;
        }
      }
      const base = kind === "journal" ? "journal-requests" : "draft-invoices";
      await API.post(`/accounts/${base}/${id}/approve`);
      toast.success(kind === "journal" ? "Journal request approved + posted" : "Invoice approved + posted to office ledger");
      loadDrafts(draftStatus, draftKind);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Approve failed");
    }
  }, [drafts, draftStatus, draftKind, loadDrafts]);

  const confirmApproveSimple = useCallback(async () => {
    if (!drAccount || !crAccount) { toast.error("Pick both debit and credit accounts"); return; }
    if (drAccount === crAccount) { toast.error("Debit and credit accounts must differ"); return; }
    try {
      await API.post(`/accounts/journal-requests/${approvingReq.id}/approve`, {
        debit_account: drAccount,
        credit_account: crAccount,
      });
      toast.success("Entry approved + posted to ledger");
      setApprovingReq(null);
      loadDrafts(draftStatus, draftKind);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Approve failed");
    }
  }, [approvingReq, drAccount, crAccount, draftStatus, draftKind, loadDrafts]);

  const deletePendingReq = useCallback(async (id) => {
    if (!window.confirm("Delete this pending entry request? This cannot be undone.")) return;
    try {
      await API.delete(`/accounts/journal-requests/${id}`);
      toast.success("Entry request deleted");
      loadDrafts(draftStatus, draftKind);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  }, [draftStatus, draftKind, loadDrafts]);

  const loadPayees = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/accounts/payees");
      setPayees(data);
      if (!coa.length) {
        const r = await API.get("/accounts/coa");
        setCoa(r.data);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  }, [coa.length]);

  const savePayee = useCallback(async () => {
    if (!payeeForm.payee.trim()) { toast.error("Payee is required"); return; }
    if (!payeeForm.debit_account || !payeeForm.credit_account) { toast.error("Pick both accounts"); return; }
    if (payeeForm.debit_account === payeeForm.credit_account) { toast.error("Debit and credit must differ"); return; }
    try {
      await API.post("/accounts/payees", payeeForm);
      toast.success(editingPayee ? "Payee updated" : "Payee added");
      setEditingPayee(null);
      setPayeeForm({ payee: "", debit_account: "", credit_account: "" });
      loadPayees();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  }, [payeeForm, editingPayee, loadPayees]);

  const deletePayee = useCallback(async (key, display) => {
    if (!window.confirm(`Remove "${display}" from the payee directory? This won't reverse any existing entries.`)) return;
    try {
      await API.delete(`/accounts/payees/${encodeURIComponent(key)}`);
      toast.success("Payee removed");
      loadPayees();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  }, [loadPayees]);

  const importPayees = useCallback(async (file) => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await API.post("/accounts/payees/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errCount = (data.errors || []).length;
      toast.success(`Imported: ${data.inserted} new + ${data.updated} updated${errCount ? ` (${errCount} skipped)` : ""}`);
      if (errCount && errCount <= 6) {
        data.errors.forEach((e) => toast.warning(e));
      } else if (errCount > 6) {
        toast.warning(`${errCount} rows skipped — first: ${data.errors[0]}`);
      }
      loadPayees();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setLoading(false); }
  }, [loadPayees]);

  const rejectDraft = useCallback(async () => {
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    try {
      const base = rejectingKind === "journal" ? "journal-requests" : "draft-invoices";
      await API.post(`/accounts/${base}/${rejectingId}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setRejectingId(null); setRejectReason("");
      loadDrafts(draftStatus, draftKind);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reject failed");
    }
  }, [rejectingId, rejectingKind, rejectReason, draftStatus, draftKind, loadDrafts]);

  useEffect(() => {
    if (tab === "trial") loadTrial();
    if (tab === "breaches") loadBreaches();
    if (tab === "unbilled") loadUnbilled();
    if (tab === "bank") loadBankRows(bankAccountCode);
    if (tab === "drafts") {
      loadDrafts(draftStatus, draftKind);
      if (!coa.length) API.get("/accounts/coa").then((r) => setCoa(r.data)).catch(() => {});
    }
    if (tab === "payees") loadPayees();
  }, [tab, bankAccountCode, draftStatus, draftKind, coa.length, loadTrial, loadBreaches, loadUnbilled, loadBankRows, loadDrafts, loadPayees]);

  const handleBankImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await API.post(`/accounts/bank-import?account_code=${bankAccountCode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported}/${data.parsed} rows${data.errors.length ? ` (${data.errors.length} errors)` : ""}`);
      loadBankRows(bankAccountCode);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const TABS = [
    { key: "drafts", label: "Draft invoices", icon: FileArrowUp },
    { key: "trial", label: "Trial balance", icon: Scales },
    { key: "breaches", label: "SRA breaches", icon: Warning },
    { key: "unbilled", label: "Unbilled disbursements", icon: Receipt },
    { key: "bank", label: "Bank import", icon: FileArrowUp },
    { key: "payees", label: "Payees", icon: Receipt },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="accounts-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-5">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Accounts</h1>
          <p className="text-sm text-slate-500 mt-0.5">Firm-wide client + office position, SRA breach log, bank statement reconciliation.</p>
        </div>

        <div className="flex items-center gap-2 mb-4 overflow-x-auto" data-testid="accounts-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`accounts-tab-${t.key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium whitespace-nowrap ${
                  tab === t.key
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                <Icon weight="bold" className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
          <button
            onClick={() => { if (tab === "trial") loadTrial(); if (tab === "breaches") loadBreaches(); if (tab === "unbilled") loadUnbilled(); if (tab === "bank") loadBankRows(bankAccountCode); }}
            className="ml-auto w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100"
            data-testid="accounts-reload-btn"
          >
            <ArrowsClockwise weight="bold" className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Trial balance */}
        {tab === "trial" && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="trial-balance-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Account</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(trial || []).map((r) => (
                  <tr key={r.code} className="border-t border-slate-100" data-testid={`tb-row-${r.code}`}>
                    <td className="px-3 py-2 font-mono">{r.code}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 capitalize text-slate-500">{r.type}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.debit ? fmt(r.debit) : ""}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.credit ? fmt(r.credit) : ""}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-medium ${r.balance < 0 ? "text-rose-600" : "text-slate-900"}`}>{fmt(r.balance)}</td>
                  </tr>
                ))}
                {trial && trial.length === 0 && (
                  <tr><td colSpan="6" className="px-3 py-6 text-center text-slate-400 italic">No postings yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* SRA breaches */}
        {tab === "breaches" && (
          <div className="space-y-4" data-testid="breaches-panel">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Overdrawn client cases</h3>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                    <tr><th className="px-3 py-2 text-left">Case ID</th><th className="px-3 py-2 text-right">Balance</th></tr>
                  </thead>
                  <tbody>
                    {(breaches?.overdrawn_cases || []).map((r) => (
                      <tr key={r.case_id} className="border-t border-slate-100">
                        <td className="px-3 py-2"><a className="text-blue-600 hover:underline" onClick={() => navigate(`/cases/${r.case_id}`)}>{r.case_id}</a></td>
                        <td className="px-3 py-2 text-right text-rose-600 font-medium tabular-nums">{fmt(r.balance)}</td>
                      </tr>
                    ))}
                    {breaches?.overdrawn_cases?.length === 0 && (
                      <tr><td colSpan="2" className="px-3 py-4 text-center text-emerald-700 italic">No overdrawn cases — good standing ✓</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Recent breach attempts (rejected)</h3>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                    <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Case</th><th className="px-3 py-2 text-left">Acct</th><th className="px-3 py-2 text-right">Would become</th><th className="px-3 py-2 text-left">Narrative</th></tr>
                  </thead>
                  <tbody>
                    {(breaches?.log || []).map((b) => (
                      <tr key={b.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 whitespace-nowrap text-slate-500">{(b.attempted_at || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="px-3 py-2 font-mono">{b.case_id?.slice(0, 8)}</td>
                        <td className="px-3 py-2 font-mono">{b.account_code}</td>
                        <td className="px-3 py-2 text-right text-rose-600 tabular-nums">{fmt(b.would_become)}</td>
                        <td className="px-3 py-2">{b.narrative}</td>
                      </tr>
                    ))}
                    {breaches?.log?.length === 0 && (
                      <tr><td colSpan="5" className="px-3 py-4 text-center text-slate-400 italic">No rejected breach attempts on record.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Unbilled disbursements */}
        {tab === "unbilled" && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="unbilled-panel">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                <tr><th className="px-3 py-2 text-left">Case</th><th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2 text-right">Recovered</th><th className="px-3 py-2 text-right">Outstanding</th></tr>
              </thead>
              <tbody>
                {(unbilled || []).map((r) => (
                  <tr key={r.case_id} className="border-t border-slate-100">
                    <td className="px-3 py-2"><a className="text-blue-600 hover:underline" onClick={() => navigate(`/cases/${r.case_id}`)}>{r.case_title || r.case_id}</a></td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.paid)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(r.recovered)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-amber-700">{fmt(r.outstanding)}</td>
                  </tr>
                ))}
                {unbilled?.length === 0 && (
                  <tr><td colSpan="4" className="px-3 py-4 text-center text-slate-400 italic">No unbilled disbursements.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Bank import */}
        {tab === "bank" && (
          <div className="space-y-3" data-testid="bank-panel">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-xs">Bank:</Label>
              <Select value={bankAccountCode} onValueChange={setBankAccountCode}>
                <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="bank-account-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1010" className="text-xs">1010 — Client Bank Account</SelectItem>
                  <SelectItem value="1020" className="text-xs">1020 — Office Bank Account</SelectItem>
                </SelectContent>
              </Select>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-md cursor-pointer" style={{ background: "#100f5d" }} data-testid="bank-import-btn">
                <FileArrowUp weight="bold" className="w-3.5 h-3.5" /> {importing ? "Importing…" : "Import CSV"}
                <input type="file" accept=".csv,text/csv" onChange={handleBankImport} className="hidden" disabled={importing} />
              </label>
              <span className="text-[11px] text-slate-400">CSV needs columns: date, description, amount, (optional balance)</span>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Matched</th>
                  </tr>
                </thead>
                <tbody>
                  {(bankRows || []).map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">{r.date}</td>
                      <td className="px-3 py-2">{r.description}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.amount < 0 ? "text-rose-600" : "text-emerald-700"}`}>{fmt(r.amount)}</td>
                      <td className="px-3 py-2">{r.matched_entry_id ? <span className="text-emerald-700">✓ matched</span> : <span className="text-slate-400">—</span>}</td>
                    </tr>
                  ))}
                  {bankRows?.length === 0 && (
                    <tr><td colSpan="4" className="px-3 py-4 text-center text-slate-400 italic">No bank rows yet. Import a CSV to begin reconciliation.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Drafts — auto-created invoices + solicitor-raised journal
            requests, awaiting accounts-team approval. Approve = post to
            the ledger; reject = lock with a reason. */}
        {tab === "drafts" && (
          <div className="space-y-3" data-testid="drafts-panel">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px]">
                {[["invoices", "Invoices"], ["journal", "Journal requests"]].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setDraftKind(k)}
                    data-testid={`draft-kind-${k}`}
                    className={`px-3 py-1 ${draftKind === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {["pending", "approved", "rejected"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setDraftStatus(s)}
                    data-testid={`draft-status-${s}`}
                    className={`px-2.5 py-1 text-[11px] font-medium rounded-md capitalize ${
                      draftStatus === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            {(drafts || []).length === 0 && (
              <p className="text-center text-xs text-slate-400 italic py-8">
                No {draftStatus} {draftKind === "journal" ? "journal requests" : "drafts"}.
                {draftKind === "journal" ? " Use the Request entry button on a case ledger to raise one." : " Generate a Client Invoice PDF on a case to create one."}
              </p>
            )}

            {/* Invoice drafts */}
            {draftKind === "invoices" && (drafts || []).map((d) => (
              <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`draft-${d.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">
                      <a className="hover:underline cursor-pointer" onClick={() => navigate(`/cases/${d.case_id}`)}>{d.case_title || d.case_id}</a>
                      <span className="ml-2 text-[10px] text-slate-400 font-mono">{d.case_ref}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Created {new Date(d.created_at).toLocaleString("en-GB")} by {d.created_by_name || "—"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>{fmt(d.totals.total)}</p>
                    <p className="text-[10px] text-slate-400">total inc. VAT</p>
                  </div>
                </div>
                <div className="mt-2 text-xs text-slate-600 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>Legal: <b>{fmt(d.totals.legal_net)}</b></div>
                  <div>VAT: <b>{fmt(d.totals.legal_vat)}</b></div>
                  <div>Disbs: <b>{fmt(d.totals.disbursements)}</b></div>
                  <div>3rd party: <b>{fmt(d.totals.third_party)}</b></div>
                </div>
                <details className="mt-2">
                  <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-800">Show line items ({d.items.length})</summary>
                  <ul className="mt-1.5 text-[11px] text-slate-600 space-y-0.5 pl-3">
                    {d.items.map((it, i) => (
                      <li key={i} className="flex justify-between">
                        <span><span className="text-slate-400 mr-1">{it.category}</span>{it.label}</span>
                        <span className="tabular-nums">{fmt(it.total)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
                {d.status === "pending" && (
                  <div className="mt-3 flex gap-2 justify-end">
                    <Button onClick={() => { setRejectingId(d.id); setRejectingKind("invoices"); setRejectReason(""); }} variant="outline" size="sm" className="h-7 text-xs" data-testid={`reject-${d.id}`}>Reject</Button>
                    <Button onClick={() => approveDraft(d.id, "invoices")} size="sm" className="h-7 text-xs text-white" style={{ background: "#100f5d" }} data-testid={`approve-${d.id}`}>Approve & post</Button>
                  </div>
                )}
                {d.status === "approved" && (
                  <p className="mt-3 text-[11px] text-emerald-700">
                    Approved {new Date(d.approved_at).toLocaleString("en-GB")} by {d.approved_by_name || "—"} → journal entry {d.journal_entry_id?.slice(0, 8)}…
                  </p>
                )}
                {d.status === "rejected" && (
                  <p className="mt-3 text-[11px] text-rose-700">
                    Rejected {new Date(d.rejected_at).toLocaleString("en-GB")}: {d.rejection_reason}
                  </p>
                )}
              </div>
            ))}

            {/* Journal-entry requests — solicitor-raised single-amount
                form. Admin picks the actual debit/credit account codes
                when approving. */}
            {draftKind === "journal" && (drafts || []).map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`journal-req-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{r.narrative}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {r.posting_date} ·
                      {r.case_id ? (
                        <> case <a className="text-blue-600 hover:underline ml-1 cursor-pointer" onClick={() => navigate(`/cases/${r.case_id}`)}>{r.case_id.slice(0, 8)}</a> ·</>
                      ) : null}
                      {" "}Created {new Date(r.created_at).toLocaleString("en-GB")} by {r.created_by_name || "—"}
                      {r.side ? ` · ${r.side} ledger` : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>{fmt(r.total_debit)}</p>
                    <p className="text-[10px] text-slate-400 capitalize">{r.simple_form?.transaction_type || ""}{r.simple_form?.method ? ` · ${r.simple_form.method}` : ""}</p>
                  </div>
                </div>
                {r.simple_form && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-slate-600">
                    <div><span className="text-slate-400">Payee:</span> <b>{r.simple_form.payee}</b></div>
                    {r.simple_form.payee_address && <div><span className="text-slate-400">Address:</span> {r.simple_form.payee_address}</div>}
                    {r.simple_form.account_number && <div><span className="text-slate-400">Account:</span> {r.simple_form.account_number}</div>}
                    {r.simple_form.sort_code && <div><span className="text-slate-400">Sort:</span> {r.simple_form.sort_code}</div>}
                    {r.simple_form.reference && <div><span className="text-slate-400">Reference:</span> {r.simple_form.reference}</div>}
                    {r.simple_form.notes && <div className="sm:col-span-2"><span className="text-slate-400">Notes:</span> {r.simple_form.notes}</div>}
                  </div>
                )}
                {/* Lines shown only AFTER approval (the simple draft has no lines until then). */}
                {r.status !== "pending" && r.lines?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-800">Show posting ({r.lines.length} lines)</summary>
                    <ul className="mt-1.5 text-[11px] text-slate-600 space-y-0.5">
                      {r.lines.map((l, i) => (
                        <li key={i} className="grid grid-cols-[60px_1fr_90px] gap-2">
                          <span className="font-mono">{l.account_code}</span>
                          <span className="truncate text-slate-500">{l.memo || l.account_name}</span>
                          <span className="tabular-nums text-right">{l.debit ? `Dr ${fmt(l.debit)}` : `Cr ${fmt(l.credit)}`}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {r.status === "pending" && (
                  <div className="mt-3 flex gap-2 justify-end">
                    <Button onClick={() => deletePendingReq(r.id)} variant="outline" size="sm" className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`delete-jreq-${r.id}`}>Delete</Button>
                    <Button onClick={() => { setRejectingId(r.id); setRejectingKind("journal"); setRejectReason(""); }} variant="outline" size="sm" className="h-7 text-xs" data-testid={`reject-jreq-${r.id}`}>Reject</Button>
                    <Button onClick={() => approveDraft(r.id, "journal")} size="sm" className="h-7 text-xs text-white" style={{ background: "#100f5d" }} data-testid={`approve-jreq-${r.id}`}>Approve & post</Button>
                  </div>
                )}
                {r.status === "approved" && (
                  <p className="mt-3 text-[11px] text-emerald-700">
                    Approved {new Date(r.approved_at).toLocaleString("en-GB")} by {r.approved_by_name || "—"} → journal entry {r.journal_entry_id?.slice(0, 8)}…
                  </p>
                )}
                {r.status === "rejected" && (
                  <p className="mt-3 text-[11px] text-rose-700">
                    Rejected {new Date(r.rejected_at).toLocaleString("en-GB")}: {r.rejection_reason}
                  </p>
                )}
              </div>
            ))}

            {/* Approval dialog — admin picks Dr + Cr accounts */}
            {approvingReq && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setApprovingReq(null)}>
                <div className="bg-white rounded-lg p-4 w-[min(560px,95vw)]" onClick={(e) => e.stopPropagation()} data-testid="approve-simple-dialog">
                  <h3 className="text-sm font-semibold mb-2">Approve & post — choose accounts</h3>
                  <p className="text-[11px] text-slate-500 mb-3">{approvingReq.narrative} · <b>{fmt(approvingReq.total_debit)}</b></p>
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Debit account *</Label>
                      <Select value={drAccount} onValueChange={setDrAccount}>
                        <SelectTrigger className="h-8 text-xs" data-testid="approve-dr-account"><SelectValue placeholder="Pick the debit nominal" /></SelectTrigger>
                        <SelectContent>
                          {coa.filter((a) => !a.archived).map((a) => (
                            <SelectItem key={a.code} value={a.code} className="text-xs">{a.code} — {a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Credit account *</Label>
                      <Select value={crAccount} onValueChange={setCrAccount}>
                        <SelectTrigger className="h-8 text-xs" data-testid="approve-cr-account"><SelectValue placeholder="Pick the credit nominal" /></SelectTrigger>
                        <SelectContent>
                          {coa.filter((a) => !a.archived).map((a) => (
                            <SelectItem key={a.code} value={a.code} className="text-xs">{a.code} — {a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2 justify-end">
                    <Button onClick={() => setApprovingReq(null)} variant="outline" size="sm" className="h-7 text-xs">Cancel</Button>
                    <Button onClick={confirmApproveSimple} size="sm" className="h-7 text-xs text-white" style={{ background: "#100f5d" }} data-testid="approve-simple-confirm">Approve & post</Button>
                  </div>
                </div>
              </div>
            )}

            {rejectingId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setRejectingId(null)}>
                <div className="bg-white rounded-lg p-4 w-[min(420px,90vw)]" onClick={(e) => e.stopPropagation()} data-testid="reject-dialog">
                  <h3 className="text-sm font-semibold mb-2">Reject {rejectingKind === "journal" ? "journal request" : "invoice draft"}</h3>
                  <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (required)" className="h-8 text-xs" autoFocus data-testid="reject-reason-input" />
                  <div className="mt-3 flex gap-2 justify-end">
                    <Button onClick={() => setRejectingId(null)} variant="outline" size="sm" className="h-7 text-xs">Cancel</Button>
                    <Button onClick={rejectDraft} size="sm" className="h-7 text-xs bg-rose-600 hover:bg-rose-700 text-white" data-testid="reject-confirm-btn">Confirm reject</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payee directory — admin-editable Dr/Cr defaults per payee.
            Seeds the approval-dialog suggestions; pre-populate before
            go-live to make the first approval as fast as the second. */}
        {tab === "payees" && (
          <div className="space-y-3" data-testid="payees-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] text-slate-500">
                {payees.length} payee{payees.length === 1 ? "" : "s"} — Dr/Cr defaults are applied automatically when approving entry requests.
              </p>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-slate-700 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50" data-testid="import-payees-btn">
                  <FileArrowUp weight="bold" className="w-3 h-3" /> Import CSV
                  <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importPayees(f); e.target.value = ""; }} />
                </label>
                <Button
                  onClick={() => { setEditingPayee(null); setPayeeForm({ payee: "", debit_account: "", credit_account: "" }); setEditingPayee({}); }}
                  size="sm"
                  className="h-7 text-xs text-white"
                  style={{ background: "#100f5d" }}
                  data-testid="add-payee-btn"
                >
                  + Add payee
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 italic">CSV columns required: <b>payee, debit_account, credit_account</b></p>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left">Payee</th>
                    <th className="px-3 py-2 text-left">Debit (Dr)</th>
                    <th className="px-3 py-2 text-left">Credit (Cr)</th>
                    <th className="px-3 py-2 text-right">Times used</th>
                    <th className="px-3 py-2 text-left">Last seen</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {payees.length === 0 && (
                    <tr><td colSpan="6" className="px-3 py-6 text-center text-slate-400 italic">No remembered payees yet. Add one or approve an entry to seed.</td></tr>
                  )}
                  {payees.map((p) => (
                    <tr key={p.payee_key} className="border-t border-slate-100" data-testid={`payee-${p.payee_key}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{p.payee_display}</td>
                      <td className="px-3 py-2 font-mono">{p.debit_account} <span className="text-slate-400 ml-1 font-sans">{p.debit_account_name}</span></td>
                      <td className="px-3 py-2 font-mono">{p.credit_account} <span className="text-slate-400 ml-1 font-sans">{p.credit_account_name}</span></td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.use_count || 0}</td>
                      <td className="px-3 py-2 text-slate-500">{p.last_used_at ? new Date(p.last_used_at).toLocaleDateString("en-GB") : "—"}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => { setPayeeForm({ payee: p.payee_display, debit_account: p.debit_account, credit_account: p.credit_account }); setEditingPayee(p); }}
                          className="text-[11px] text-blue-600 hover:underline mr-3"
                          data-testid={`edit-payee-${p.payee_key}`}
                        >Edit</button>
                        <button
                          onClick={() => deletePayee(p.payee_key, p.payee_display)}
                          className="text-[11px] text-rose-600 hover:underline"
                          data-testid={`delete-payee-${p.payee_key}`}
                        >Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {editingPayee && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditingPayee(null)}>
                <div className="bg-white rounded-lg p-4 w-[min(540px,95vw)]" onClick={(e) => e.stopPropagation()} data-testid="payee-dialog">
                  <h3 className="text-sm font-semibold mb-3">{editingPayee.payee_key ? "Edit payee" : "Add payee"}</h3>
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Payee name *</Label>
                      <Input value={payeeForm.payee} onChange={(e) => setPayeeForm({ ...payeeForm, payee: e.target.value })} placeholder="e.g. HM Land Registry" className="h-8 text-xs" disabled={!!editingPayee.payee_key} data-testid="payee-name-input" />
                      {editingPayee.payee_key && <p className="text-[10px] text-slate-400">Name is the key — to rename, delete and re-create.</p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Default Debit (Dr) account *</Label>
                      <Select value={payeeForm.debit_account} onValueChange={(v) => setPayeeForm({ ...payeeForm, debit_account: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid="payee-dr-select"><SelectValue placeholder="Pick a nominal" /></SelectTrigger>
                        <SelectContent>
                          {coa.filter((a) => !a.archived).map((a) => (<SelectItem key={a.code} value={a.code} className="text-xs">{a.code} — {a.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Default Credit (Cr) account *</Label>
                      <Select value={payeeForm.credit_account} onValueChange={(v) => setPayeeForm({ ...payeeForm, credit_account: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid="payee-cr-select"><SelectValue placeholder="Pick a nominal" /></SelectTrigger>
                        <SelectContent>
                          {coa.filter((a) => !a.archived).map((a) => (<SelectItem key={a.code} value={a.code} className="text-xs">{a.code} — {a.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2 justify-end">
                    <Button onClick={() => setEditingPayee(null)} variant="outline" size="sm" className="h-7 text-xs">Cancel</Button>
                    <Button onClick={savePayee} size="sm" className="h-7 text-xs text-white" style={{ background: "#100f5d" }} data-testid="payee-save-btn">Save</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {loading && <p className="text-center text-xs text-slate-400 mt-4">Loading…</p>}
      </main>
    </div>
  );
}
